"use client";

import React, { useState, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import HomeServiceForm from "./HomeServiceForm";
import TrackBookingForm from "./TrackBookingForm";

export default function HomeServiceApp() {
  const [activeTab, setActiveTab] = useState("book"); // "book" | "track"
  const [agreed, setAgreed] = useState(false);
  const formRef = useRef(null);

  const handleAgree = () => {
    setAgreed(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <main
      className="min-h-screen w-full flex flex-col md:flex-row bg-[#F0F4FF]"
      dir="rtl"
    >
      {/* LEFT PANEL – Brand Identity (stretched on desktop) - Visually Right */}
      <section className="relative z-30 md:w-5/12 lg:w-[45%] w-full min-h-[100dvh] overflow-hidden shadow-2xl flex flex-col">
        <Image
          src="/barsavoshMain.webp"
          alt="سرویس در محل برساوش"
          fill
          className="object-cover"
          priority
        />

        {/* Gradient overlay */}
        <div className={`absolute inset-0 transition-all duration-500 ${!agreed ? "bg-[#2b2b2b]/95 backdrop-blur-md" : "bg-gradient-to-t from-[#2b2b2b] via-[#2b2b2b]/80 to-transparent"}`} />

        {/* Text content (sticky viewport on desktop) */}
        <div className={`relative z-10 flex flex-col flex-1 md:sticky md:top-0 md:h-screen ${!agreed ? 'p-6 md:p-10 lg:p-14 md:justify-center' : 'justify-center p-6 md:p-10 lg:p-14 pointer-events-none'}`}>
          {!agreed ? (
            <div className="flex flex-col flex-1 md:flex-none space-y-4 md:space-y-5 text-white pt-2 pb-6 mt-18 md:mt-0">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-black mb-4 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-gray-400 animate-pulse shrink-0" />
                قوانین و مقررات سرویس در محل
              </h2>

              <ul className="space-y-3 text-xs sm:text-sm leading-relaxed text-slate-200 list-disc list-inside marker:text-gray-400 text-justify">
                <li className="text-yellow-400 font-bold text-sm sm:text-base bg-[#2b2b2b]/60 p-3 rounded-xl border border-yellow-400/50 list-none flex items-start gap-2 shadow-lg mb-4">
                  <span className="shrink-0 mt-0.5">⚠️</span>
                  <span>
                    تا اطلاع ثانوی خدمات سرویس در محل فقط در <strong>استان تهران</strong> و <strong>استان البرز</strong> امکان پذیر می باشد.
                  </span>
                </li>
                <li>خدمات سرویس در محل، صرفا در پارکینگ منزل یا پارکینگ محل کار مشتری قابل انجام خواهد بود.</li>
                <li>صرفا سرویس های ادواری سطح (L1) مشخص شده در جدول سرویس های ادواری توسط واحد Home Service قابل انجام خواهد بود.</li>
                <li>در موارد قابل ارائه توسط واحد Home Service نظر به ضرورت انجام هماهنگی های لازم قبل از مراجعه به محل مشتری (شامل تامین قطعات، اخذ آدرس مشتری، تعیین زمان مراجعه و ...،) لازم است مشتریان محترم حداقل 24ساعت قبل از زمان مورد نظر، نسبت به اخذ نوبت اقدام نمایند.</li>
                <li>با توجه به پروتکل های ایمنی خودرو های برقی، سرویس در محل برای خودرو های BZ3 و BZ4 میسر نمی باشد لذا خواهشمند است برای رزرو نوبت به  <Link href="https://www.barsavosh.co/repairshopReserve" target="_blank" className="text-gray-300 hover:underline">نوبت دهی اینترنتی تعمیرگاه  </Link>  مراجعه نمایید.</li>
                <li>در خصوص نوبت دهی سرویس در محل (HOME SERVICE) به استحضار میرساند زمانهای انتخاب شده در سیستم تقریبی بوده و به دلیل شرایط ترافیکی، بازه زمانی حضور تکنسین ممکن است تا ۳۰ دقیقه با زمان انتخابی اختلاف داشته باشد.</li>
                <li>کیفیت و پوشش خدمات ارائه‌شده در محل مشتری، کاملاً منطبق بر استانداردهای نمایندگی بوده و تنها تفاوت آن با مراجعه حضوری، پرداخت هزینه ایاب و ذهاب تکنسین اعزامی می‌باشد.</li>
              </ul>
              <div className="mt-auto pt-6 border-t border-white/20">
                <button
                  onClick={handleAgree}
                  className="btn-large btn-theme-primary w-full sm:w-auto"
                >
                  مطالعه کردم و موافقم
                </button>
              </div>
            </div>
          ) : (
            <div className="max-w-sm space-y-3 md:space-y-5 mt-12 md:mt-0">
              {/* Badge */}
              <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                خدمات ویژه برساوش
              </span>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight">
                {activeTab === "book" ? "سرویس در " : "پیگیری "}
                <span className="text-gray-300">
                  {activeTab === "book" ? "محل" : "نوبت"}
                </span>
              </h1>

              {/* Feature bullets */}
              {activeTab === "book" && (
                <ul className="space-y-2 pt-2 hidden md:block">
                  {[
                    "بدون نیاز به مراجعه حضوری",
                    "کارشناسان تخصصی در محل شما",
                    "زمان‌بندی انعطاف‌پذیر",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-slate-200">
                      <span className="w-4 h-4 rounded-full bg-gray-400/30 border border-gray-400/50 flex items-center justify-center shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              {activeTab === "track" && (
                <p className="text-sm text-slate-200 pt-2 hidden md:block leading-relaxed">
                  با وارد کردن کد پیگیری خود، می‌توانید وضعیت نوبت را مشاهده کرده و یا در صورت نیاز آن را لغو نمایید. همچنین لوکیشن ثبت شده روی نقشه قابل مشاهده است.
                </p>
              )}

              <div className="pt-4 pointer-events-auto">
                <button
                  onClick={() => setAgreed(false)}
                  className="text-xs text-gray-300 hover:text-white underline underline-offset-4 transition-colors"
                >
                  مشاهده مجدد قوانین
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* RIGHT PANEL – Form (Visually Left) */}
      <section ref={formRef} className={`flex-1 flex flex-col items-center justify-start px-4 pb-8 pt-4 md:py-12 md:px-8 lg:px-16 relative -mt-6 sm:-mt-10 md:mt-0 z-20 transition-all duration-500 ${!agreed ? 'opacity-40 grayscale pointer-events-none' : ''}`}>
        {/* Decorative blobs */}
        <div className="absolute top-10 left-10 w-64 h-64 bg-gray-200/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-gray-300/30 rounded-full blur-3xl pointer-events-none" />

        {/* Toggle Nav */}
        <div className="relative z-10 w-full flex justify-center mt-8 mb-3">
          <div className="flex w-full max-w-sm sm:max-w-md bg-white rounded-small-button-mob md:rounded-small-button-desk p-1 border border-gray-200 shadow-sm">
            <button
              onClick={() => setActiveTab("book")}
              className={`flex-1 btn-small ${activeTab === "book"
                ? "btn-theme-active"
                : "btn-theme-ghost"
                }`}
            >
              ثبت نوبت در محل
            </button>
            <button
              onClick={() => setActiveTab("track")}
              className={`flex-1 btn-small ${activeTab === "track"
                ? "btn-theme-active"
                : "btn-theme-ghost"
                }`}
            >
              پیگیری / لغو نوبت
            </button>
          </div>
        </div>

        <div className="relative z-10 w-full transition-all mt-4">
          {activeTab === "book" ? <HomeServiceForm /> : <TrackBookingForm />}
        </div>
      </section>
    </main>
  );
}
